import React, { useCallback, useState, useEffect, useMemo } from "react";
import { css } from "glamor";
import {useMutation, useQuery} from "@apollo/client";
import pms from "ms";
import { Button, } from "../../components/Button";
import { Container } from "../../components/Container";
import { H2, } from "../../components/Typography";
import { Loading, LoadingError } from "../../components/LoadingFork";
import { Well } from "../../components/Well";
import { withInstallation } from "../../components/InstallationContext";
import * as theme from "../../theme";
import { dropDuplicates } from "../../lib/rateCardUtils";
import { toBoolean } from "../../lib/misc";
import { differenceInMilliseconds, } from "date-fns";
import { listEvents, listEventsWithWalkupPins } from "./queries";
import { Table, TBody, THead } from '../../components/Table';
import Grid from '@material-ui/core/Grid'
import { fade, makeStyles } from "@material-ui/core/styles";
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import InputBase from "@material-ui/core/InputBase";
import SearchIcon from "@material-ui/icons/Search";
import Typography from "@material-ui/core/Typography";
import { CellPill } from '../../components/StatusPill'
import { Left, Right } from "../../components/Header";
import { DateTime } from "luxon";
import authManager, {PERMISSIONS} from '../../lib/authManager';
import { hideGlobalLoader, showGlobalLoader } from "../../lib/loaderController";
import {PermissionOnly, RootOnly} from "../../components/AdminOnly";
import { updateFeatureFacilityProviderConfigurationMutation} from "../Settings/RateCards/queries";
import { debounce } from "lodash";
import { isWalkupPinEnabled } from "../../lib/walkupPinFeature";
import WalkupPinCell from "./components/WalkupPinCell";
import useWalkupPinRefresh from "./useWalkupPinRefresh";

const useStyles = makeStyles((theme) => ({
    card: {
        height: '90vh',
        overflowY: 'auto',
        borderRadius: '14px',
        background: '#fff',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow,.3s -webkit-transform cubic-bezier(.155,1.105,.295,1.12)',
        padding: '35px 35px 45px 15px',
    },
    search: {
        position: 'relative',
        borderColor: '#91b35c',
        borderStyle: 'solid',
        borderRadius: 30,
        backgroundColor: fade('#eaeaea', 0.35),
        '&:hover': {
            backgroundColor: fade('#eaeaea', 0.55),
        }
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '65ch',
            height: '5ch'
        },
    },
    root: {
        height: '44rem',
        overflowY: 'auto',
        borderRadius: '15px',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow,.3s -webkit-transform cubic-bezier(.155,1.105,.295,1.12)',
        padding: '8px 7px 30px 5px',
        '&:hover': {
            transform: ' scale(1.01)',
            boxShadow: '0 10px 20px rgba(0,0,0,.12), 0 4px 8px rgba(0,0,0,.06)'
        }

    },
    media: {
        height: 0,
        paddingTop: '30.25%', // 16:9,
        borderRadius: '15px',
        margin: '10px',
    },
    filter: {
        height: 60,
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        imageRendering: 'auto', // might help browsers resize the image crisply
        cursor: 'pointer'
    }
}));

const ROW_STYLE = css({
    "> td": {
        verticalAlign: "middle",
        height: 60,
        textAlign: "center",
        borderBottom: `1px solid ${theme.gray10}`,
        transition: "all .2s linear",
    },
    ":hover > td": {
        background: theme.gray10,
        cursor: "pointer",
    },
});

export function SearchBar({ onSearch, searchTerm }) {
    const classes = useStyles();
    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon color="primary" />
                        </div>
                        <InputBase
                            autoFocus
                            placeholder="Search for a licence plate"
                            onChange={onSearch}
                            value={searchTerm}
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                            inputProps={{ 'aria-label': 'search' }}
                        />
                    </div>
                </Grid>
            </Grid>
        </>
    )
}
function Header(props) {
    return <div
        className={css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
        })}
        {...props}
    />
}

export function isToday(date, timezone) {
    if (!date) return false;
    return date.hasSame?.(DateTime.now().setZone(timezone), "day") ?? false
}

export function isYesterday(date, timezone) {
    if (!date) return false;
    return date.hasSame?.(DateTime.now().setZone(timezone).minus({ days: 1 }), "day") ?? false
}

export function calendarDate(date, timezone) {
    if (isToday(date, timezone)) return "Today";
    if (isYesterday(date, timezone)) return "Yesterday";
    return date.toFormat("yyyy/MM/dd");
}

const cellStyle = css({
    fontWeight: '600',
    textAlign: "center",
    padding: '0 10px',
})
const tableStyle = css({ width: "100%", textAlign: 'left', tableLayout: "fixed", });

export const getFeatureConfiguration = (configurations, featureName) => {
    return configurations.find(({ feature }) => feature.name === featureName);
}

export function extractGateShortLabel(text) {
    if (!text) return '';

    const normalized = text.toLowerCase();
    const match = normalized.match(/\b(entry|exit)\s*(\d+)\b/);
    if (match) {
        return `${match[1]} ${match[2]}`;
    }

    return text;
}

/*
 * A walk-up entry is never folded into a neighbouring duplicate: its PIN
 * belongs to that entry alone and would otherwise vanish from the list.
 */
export function collapseDuplicateEntries(items) {
    const kept = new Set(dropDuplicates(items, 3000));
    const shownPinIds = new Set(
        items.filter((event) => kept.has(event) && event.pinpadAccess).map((event) => event.pinpadAccess.id)
    );

    return items.filter((event) => {
        if (kept.has(event)) return true;
        const pinId = event.pinpadAccess?.id;
        if (!pinId || shownPinIds.has(pinId)) return false;
        shownPinIds.add(pinId);
        return true;
    });
}

function NewBooking({ installation, facility, history, match }) {
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [operaConfig, setOperaConfig] = useState(getFeatureConfiguration(facility?.featureFacilityProviderConfigurations || [], 'OPERA_INTEGRATION'));
    const walkupPinsEnabled = isWalkupPinEnabled(facility);
    const columnWidth = `${100 / (walkupPinsEnabled ? 6 : 5)}%`;
    const [updateOperaIntegration, { error: updateError }] = useMutation(updateFeatureFacilityProviderConfigurationMutation, {
        errorPolicy: 'all',
    })

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value) => setDebouncedSearch(value), 300),
        []
    );

    useEffect(() => {
        return () => debouncedSetSearch.cancel();
    }, [debouncedSetSearch]);

    const { data = {}, loading, error, fetchMore } = useQuery(
        walkupPinsEnabled ? listEventsWithWalkupPins : listEvents,
        {
            variables: {
                offset: 0,
                search: debouncedSearch || undefined,
                gateType: 'ENTRY',
            },
            fetchPolicy: "network-only",
        }
    );

    const events = collapseDuplicateEntries(data.listEvents?.items || []);
    useWalkupPinRefresh(events);
    const eventsCount = data.listEvents?.count || 0;
    const moreToLoad = events.length < eventsCount;

    const loadMore = useCallback(() => {
        fetchMore({
            variables: {
                offset: events.length,
                search: debouncedSearch || undefined,
            },
        });
    }, [fetchMore, events.length, debouncedSearch]);

    function onSearch(e) {
        const value = e.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase();
        setSearchTerm(value);
        debouncedSetSearch(value);
    }

    function onEntrySelect(eventId, installationId) {
        if (!authManager.hasPermission(PERMISSIONS.BOOKINGS_MANAGE, installationId)) {
            return;
        }

        history.push(
            match.url.replace("/new-booking", `/bookings/create?eventId=${eventId}`)
        );
    }

    function onCreate() {
        history.push(
            match.url.replace("/new-booking", `/bookings/create?plate=${searchTerm}`)
        );
    }

    async function triggerOHIP() {
        if (!operaConfig) {
            console.error('OHIP update cant be triggered - config is not provided for this Installation');
            return;
        }

        showGlobalLoader();
        const newConfiguration = {
            ...operaConfig.configuration,
            operaIntegrationEnabled: !toBoolean(operaConfig.configuration.operaIntegrationEnabled),
        };

        try {
            const { errors } = await updateOperaIntegration({
                variables: {
                    id: operaConfig.id,
                    configuration: newConfiguration,
                },
            });

            if (errors?.length) {
                console.error("GraphQL errors:", errors);
                return;
            }

            setOperaConfig({
                ...operaConfig,
                configuration: newConfiguration,
            });
        } catch (e) {
            console.error("Mutation failed:", e);
        } finally {
            hideGlobalLoader();
        }
    }

    useEffect(() => {
        const intervalId = setInterval(async () => {
            await authManager.updateAuthState();
            if (authManager.isAuthenticated) {
                loadMore()
            }
        }, 30 * 1000);

        return () => clearInterval(intervalId);

    }, [loadMore, authManager.isAuthenticated])

    if (error) {
        console.error("Error loading events", error);
    }

    function EventRow({ event, onClick }) {
        const { id, plate, gateDescription, pcLoggedAt } = event;
        const eventAt = new Date(pcLoggedAt);
        const eventDateTime = DateTime.fromISO(eventAt.toISOString(), { locale: "en-AU" }).setZone(installation.timeZoneName)
        const ms = differenceInMilliseconds(new Date(), eventAt);

        return (
            <tr className={ROW_STYLE} onClick={() => onClick(id)}>
                <td style={cellStyle}>
                    <CellPill color='#37B534' size='small'>{plate}</CellPill>
                </td>
                {walkupPinsEnabled ? <WalkupPinCell pinpadAccess={event.pinpadAccess} style={cellStyle} /> : null}
                <td style={cellStyle}>
                    <CellPill color='#FFA800' size='small'>{extractGateShortLabel(gateDescription)}</CellPill>
                </td>
                <td style={cellStyle}>
                    <CellPill color='#FF4773' size='small'>{calendarDate(eventDateTime, installation.timeZoneName)}</CellPill>
                </td>
                <td style={cellStyle}>
                    <CellPill color='#45B5F4' size='small'>{eventDateTime.toFormat("HH:mm ZZZZ")}</CellPill>
                </td>
                <td>{pms(ms, { long: true })} ago</td>
            </tr>
        );
    }

    return (
        <div>
            <Card className={classes.card}>
                <CardMedia
                    className={classes.media}
                    image="/parking.jpg"
                    title="parking"
                />

                <CardContent>
                    <Header>
                        <Left>
                            <Typography variant={'h5'}>License Plate Entries Without Bookings</Typography>
                        </Left>
                        <Right>
                            { operaConfig && (
                                <RootOnly>
                                    <Button onClick={triggerOHIP} className={css({ marginRight: '1rem' })} >
                                        {toBoolean(operaConfig?.configuration?.operaIntegrationEnabled) ? 'Disable ' : 'Enable '}
                                        OHIP
                                    </Button>
                                </RootOnly>
                            ) }
                            <PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
                                <Button onClick={onCreate} >New Booking</Button>
                            </PermissionOnly>
                        </Right>
                    </Header>

                    { updateError && (
                        <Typography color="error" style={{ marginBottom: '1rem' }}>
                            {"An error occurred while updating Opera integration."}
                        </Typography>
                    )}

                    <Typography variant="subtitle1" color="textSecondary">
                        Select an entry below to create a new booking
                    </Typography>
                    <br />
                    <SearchBar
                        onSearch={onSearch}
                        searchTerm={searchTerm}
                    />
                    <br />
                    <Container size="full" spaceBelow="small">
                        {error ? <LoadingError error={error} /> : null}
                        {events.length === 0 ? (
                            <Well>
                                <div>
                                    <H2 spaceAbove="large" spaceBelow="large">
                                        {loading ? <Loading /> : "No results available"}
                                    </H2>
                                </div>
                            </Well>
                        ) : (

                            <Table className={tableStyle}>
                                <THead>
                                    <tr style={{ color: '#3127B8', textAlign: "center" }}>
                                        <th style={{ width: columnWidth }}>License Plate</th>
                                        {walkupPinsEnabled ? <th style={{ width: columnWidth }}>PIN</th> : null}
                                        <th style={{ width: columnWidth }}>Gate</th>
                                        <th style={{ width: columnWidth }}>Date</th>
                                        <th style={{ width: columnWidth }}>Time</th>
                                        <th style={{ width: columnWidth }}>Ago</th>
                                    </tr>
                                </THead>

                                <TBody>
                                    {events.map((event) => {
                                        return (
                                            <EventRow
                                                key={event.id}
                                                event={event}
                                                onClick={() => onEntrySelect(event.id, installation.id)}
                                            />
                                        );
                                    })}
                                </TBody>
                            </Table>
                        )}

                        <div className={css({ textAlign: "center" })}>
                            {moreToLoad ? (
                                <Button onClick={loadMore} style={{ backgroundColor: '#3127B8' }} spaceAbove="medium"
                                        spaceBelow="medium">
                                    Fetch More
                                </Button>
                            ) : null}
                        </div>

                    </Container>
                </CardContent>
            </Card>
        </div>
    );
}

export default withInstallation(NewBooking);
